import { UserProfile } from '../models/userProfile';
import { Auth } from 'aws-amplify';
import React, { useEffect, useRef, useState } from 'react';
import { Post } from '../models/post';
import { useAppState } from '../context/AppStateContext';
import { PostDataService } from '../services/postDataService';
import { UserDataService } from '../services/userDataService';
import { ChildProfileDataService } from '../services/childProfileDataService';
import { Navigate, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { ViewModeEnum, ViewMode } from '../types/viewMode';
import { getMonday, getFriday, toDateString } from '../services/dateUtils';
import { LayoutOutletContext } from '../components/Layout';
import { pathToView, viewToPath } from '../utils/viewRouting';
import { useHelpNavigationGuard } from '../help/HelpProvider';
import { getSelectedOrgId } from '../services/selectedOrg';

// Components
import SidebarFilters, { DateShortcut } from '../components/SidebarFilters';
import PostsGrid from '../components/PostsGrid';
import ChildProfilesSidebar from '../components/ChildProfilesSidebar';
import ChildSelectorPanel from '../components/ChildSelectorPanel';
import ScheduleTeacherFilter from '../components/ScheduleTeacherFilter';
import ConfirmationModal from '../components/ConfirmationModal';
import ReportsTabs from '../components/report/ReportsTabs';
import WeeklySchedule from '../components/schedule/WeeklySchedule';
import WeeklyAttendance from '../components/schedule/WeeklyAttendance';
import Dashboard from '../components/dashboard/Dashboard';
import AttendanceCharts from '../components/charts/AttendanceCharts';
import DocumentsTabs from '../components/documents/DocumentsTabs';
import ChildInsights from '../components/charts/ChildInsights';

const Home: React.FC = () => {
  const {
    user, setUser,
    userRoleIds, setUserRoleIds,
    roles, setRoles,
    childProfiles, setChildProfiles,
    activeUser,
    selectedOrganisation, setSelectedOrganisation,
    setOrgConfig,
    setEntitlements,
    entitlements,
  } = useAppState();
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarOpen, setSidebarOpen } = useOutletContext<LayoutOutletContext>();

  const [posts, setPosts] = useState<Post[]>([]);
  // The posts grid doubles as the restore view: same grid, same cards, filtered
  // to posts still inside their delete window. A separate list would have meant
  // rendering a post in a second, thinner way that nobody could recognise.
  const [showingDeleted, setShowingDeleted] = useState(false);
  const [isReloading, setIsReloading] = useState<boolean>(false);
  const [filterChild, setFilterChild] = useState<string | null>(null);
  const [filterTeacherId, setFilterTeacherId] = useState<number | null>(null);
  const [filterFromDate, setFilterFromDate] = useState<string | null>(null);
  const [filterToDate, setFilterToDate] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  // True when the Documents view is showing its Organisation tab — the child
  // selector slides away there (org docs aren't child-scoped).
  const [documentsOnOrgTab, setDocumentsOnOrgTab] = useState(false);
  // True when the Reports view is showing its Report History tab — that tab
  // treats the child as an optional filter, so it never demands a selection.
  const [reportsOnHistoryTab, setReportsOnHistoryTab] = useState(false);
  // Set by the sidebar's compact/full toggle: browse the children full screen
  // even though one is already selected. Cleared as soon as a child is picked.
  const [childPickerOpen, setChildPickerOpen] = useState(false);

  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  // viewMode is purely derived from the URL — each section has its own route
  // (/posts, /schedule, /attendance, /attendance-stats, /reports). Browser
  // back/forward and direct links now work naturally.
  const viewMode = pathToView(location.pathname);
  const setViewMode = (mode: ViewMode) => navigate(viewToPath(mode));
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [shortcut, setShortcut] = useState<DateShortcut>('week');

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const confirmResolveRef = useRef<((value: boolean) => void) | null>(null);
  const [postSaveMessage, setPostSaveMessage] = useState<string | null>(null);
  const [reportGenerateMessage, setReportGenerateMessage] = useState<string | null>(null);


  useEffect(() => {
    if (postSaveMessage) {
      const t = setTimeout(() => setPostSaveMessage(null), 3000);
      return () => clearTimeout(t);
    }
  }, [postSaveMessage]);

  useEffect(() => {
    if (reportGenerateMessage) {
      const t = setTimeout(() => setReportGenerateMessage(null), 3000);
      return () => clearTimeout(t);
    }
  }, [reportGenerateMessage]);

  // Moving between sections drops any "browse full screen" request — the new
  // section decides for itself whether the picker is needed. The tabbed
  // sections remount on their first tab and re-announce it, but clear the
  // flags here too: a stale value left over from the last visit renders the
  // child column for a frame before the tab report collapses it again.
  useEffect(() => {
    setChildPickerOpen(false);
    setReportsOnHistoryTab(false);
    setDocumentsOnOrgTab(false);
  }, [viewMode]);

  // True on the first render of a section (including mount), where the child
  // column must take its width immediately rather than sliding to it. The
  // effect re-renders once the width has been painted, so in-section changes
  // (picking a child, toggling the picker) still animate.
  const [settledView, setSettledView] = useState<ViewMode | null>(null);
  const sectionJustChanged = settledView !== viewMode;
  useEffect(() => {
    if (sectionJustChanged) setSettledView(viewMode);
  }, [sectionJustChanged, viewMode]);

  useEffect(() => {
    const init = async () => {
      await fetchRoles();
      // A support session has no member profile to look up (the API refuses
      // the lookup and logs it as support having tried); the session itself
      // says who this is.
      let userProfile: UserProfile | null;
      if (activeUser?.isSupport) {
        userProfile = { id: activeUser.id, name: activeUser.name, email: activeUser.email };
        setUser(userProfile);
      } else {
        userProfile = await fetchUserEmail();
      }
      // PIN user's roles are authoritative — set them and use for child profile filtering
      const roleIds = activeUser?.roleIds ?? [];
      setUserRoleIds(roleIds);

      // Hydrate selectedOrganisation BEFORE fetching child profiles so we
      // know whether the current user is external in this org. On a fresh
      // page load selectedOrganisation may be null (only the stored org ID
      // survives a refresh) — we need the full Organisation object back.
      let activeOrg = selectedOrganisation;
      if (!activeOrg) {
        const storedId = getSelectedOrgId();
        if (storedId) {
          try {
            const orgs = await UserDataService.fetchOrganisations();
            const match = orgs.find(o => o.id === parseInt(storedId, 10));
            if (match) {
              setSelectedOrganisation(match);
              activeOrg = match;
            }
          } catch (err) {
            console.error('Failed to rehydrate selected organisation', err);
          }
        }
      }

      // Source of truth for "is the *current* actor external":
      //   1. PIN-active user's flag (when one is set — overrides the device user)
      //   2. Cognito user's flag for this org (from selectedOrganisation)
      // Same precedence for the user id whose whitelist we fetch — the
      // server's /child-whitelist endpoint only lets a non-admin query their
      // own whitelist, and "their own" means the active user.
      const actorIsExternal =
        activeUser?.isExternal === true ||
        (activeUser?.isExternal === undefined && activeOrg?.isExternal === true);
      const actorUserId = activeUser?.id ?? userProfile?.id;
      if (actorUserId) {
        await fetchChildProfiles(actorIsExternal, actorUserId);
      }

      // Load per-org feature config (schedule grid + attendance defaults +
      // report Setting label). Cached on context so all consumers read
      // from one place. Failure is non-fatal — consumers fall back to the
      // safe defaults exported from models/orgConfig.
      try {
        const cfg = await UserDataService.fetchOrgConfig();
        setOrgConfig(cfg);
      } catch (err) {
        console.error('Failed to load org config', err);
      }

      // What this org's plan includes — decides whether Insights and the
      // focus-area tray render at all. Fetched here alongside the config so
      // both land before the nav paints. Failure leaves the fail-closed
      // default: a premium surface must never flash in and then vanish.
      try {
        setEntitlements(await UserDataService.fetchEntitlements());
      } catch (err) {
        console.error('Failed to load entitlements — treating plan as basic', err);
      }
    };
    init();
  }, []);

  // Sync selectedPost with posts updates (e.g. after reload/save)
  useEffect(() => {
    if (selectedPost && posts.length > 0) {
      const updated = posts.find(p => p.id === selectedPost.id);
      // Only update if reference changed to avoid loop, and check timestamps to ensure we don't revert to old data
      if (updated && updated !== selectedPost) {
        setSelectedPost(updated);
      }
    }
  }, [posts]);


  const fetchUserEmail = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const email = user.attributes?.email;
      return await fetchUserProfile(email);
    } catch (err) {
      console.error('Failed to fetch user email address', err);
      return null;
    }
  };

  const fetchUserProfile = async (email: string) => {
    try {
      const data = await UserDataService.fetchUserProfile(email);
      setUser(data);
      return data;
    } catch (err) {
      console.error('Error fetching user profile:', err);
      return null;
    }
  };

  const fetchRoles = async () => {
    try {
      const rolesMap = await UserDataService.fetchRolesMap();
      setRoles(rolesMap);
      return rolesMap;
    } catch (err) {
      console.error('Failed to fetch roles mapping', err);
      return {};
    }
  };

  const fetchChildProfiles = async (isExternal: boolean, userId?: number) => {
    try {
      let data = await ChildProfileDataService.getChildProfiles();

      // Whitelist filter applies when:
      //   - user is external (always — empty whitelist = sees nothing); OR
      //   - any user (incl. internal) has a non-empty whitelist set.
      // Internal users with no whitelist see every child in the org.
      if (userId) {
        try {
          const whitelist = await UserDataService.fetchChildWhitelist(userId);
          if (isExternal || whitelist.length > 0) {
            data = data.filter(c => whitelist.includes(c.id));
          }
        } catch (error) {
          console.error('Error fetching child whitelist:', error);
          // Fail safe — external user with no whitelist sees nothing.
          if (isExternal) data = [];
        }
      }

      // sort by initials (case-insensitive) before setting
      const sorted = data.slice().sort((a, b) =>
        (a.initials || '').toLowerCase().localeCompare((b.initials || '').toLowerCase())
      );
      setChildProfiles(sorted);
    } catch (err) {
      console.error('Error fetching child profiles:', err);
    }
  };

  const fetchPosts = async (childId: string, fromDate?: string | null) => {
    try {
      setIsReloading(true);
      const data = await PostDataService.getPosts(
        childId,
        // The deleted view is bounded by deleted_at, not by the date window the
        // grid is browsing — a post written in March and deleted yesterday is
        // exactly what someone is looking for.
        showingDeleted ? null : (fromDate || filterFromDate || undefined),
        showingDeleted,
      );
      setPosts(data);
    } catch (err) {
      console.error('Error fetching posts:', err);
    } finally {
      setIsReloading(false);
    }
  };

  const handleViewModeChange = async (mode: ViewMode) => {
    const proceed = await confirmDiscard();
    if (!proceed) return;
    setViewMode(mode);
    setHasUnsavedChanges(false);

    if (mode === ViewModeEnum.Report || mode === ViewModeEnum.Schedule || mode === ViewModeEnum.Attendance) {
      setSelectedPost(null);
      let monday: string;
      let friday: string;

      if (filterToDate) {
        const toDate = new Date(filterToDate + 'T00:00:00');
        const fridayOfWeek = toDateString(getFriday(toDate));
        if (fridayOfWeek === filterToDate) {
          monday = toDateString(getMonday(toDate));
          friday = filterToDate;
        } else {
          const now = new Date();
          monday = toDateString(getMonday(now));
          friday = toDateString(getFriday(now));
        }
      } else {
        const now = new Date();
        monday = toDateString(getMonday(now));
        friday = toDateString(getFriday(now));
      }

      setFilterFromDate(monday);
      setFilterToDate(friday);
      if (mode === ViewModeEnum.Report && filterChild) {
        await fetchPosts(filterChild, monday);
      }
    }
  };

  // Sidebar links. View-mode destinations stay on this page and go through
  // handleViewModeChange; Admin Dashboard leaves Home altogether, so it gets
  // the same discard gate and then a plain navigate.
  const handleSidebarNavigate = async (path: string, mode: ViewMode | null) => {
    if (mode !== null) {
      await handleViewModeChange(mode);
      return;
    }
    const proceed = await confirmDiscard();
    if (!proceed) return;
    setHasUnsavedChanges(false);
    navigate(path);
  };

  const handleWeekChange = async (monday: string, friday: string) => {
    setFilterFromDate(monday);
    setFilterToDate(friday);
    if (filterChild) {
      await fetchPosts(filterChild, monday);
    }
  };

  // Reports-screen period navigation — same shape as handleWeekChange but
  // period-agnostic (day/week/month/custom per the selected report template).
  // Custom ranges can hold a cleared (null) bound; posts reload only once a
  // start date exists.
  const handlePeriodChange = async (startDate: string | null, endDate: string | null) => {
    setFilterFromDate(startDate);
    setFilterToDate(endDate);
    if (filterChild && startDate) {
      await fetchPosts(filterChild, startDate);
    }
  };

  const handleChildChange = async (child: string | null) => {
    const proceed = await confirmDiscard();
    if (!proceed) return;
    setPosts([]);
    setFilterChild(child);
    setSelectedPost(null);
    setChildPickerOpen(false);
    if (viewMode !== ViewModeEnum.Report && viewMode !== ViewModeEnum.Schedule && viewMode !== ViewModeEnum.Attendance && viewMode !== ViewModeEnum.AttendanceStats && viewMode !== ViewModeEnum.Documents && viewMode !== ViewModeEnum.Insights) {
      setViewMode(ViewModeEnum.Grid);
    }
    setHasUnsavedChanges(false);
    if (child) {
      await fetchPosts(child);
    }
  };

  // Compact → full-screen child browsing. The picker replaces the section
  // body, so an in-progress post edit would be torn down: same discard prompt
  // the child/date filters use.
  const handleExpandChildPicker = async () => {
    const proceed = await confirmDiscard();
    if (!proceed) return;
    // Discarded edits must not come back when the picker is closed again —
    // drop the post along with the dirty flag.
    if (hasUnsavedChanges) {
      setSelectedPost(null);
      setHasUnsavedChanges(false);
    }
    setChildPickerOpen(true);
  };

  const handleFromDateChange = async (date: string | null) => {
    const proceed = await confirmDiscard();
    if (!proceed) return;
    setPosts([]);
    setFilterFromDate(date);
    setHasUnsavedChanges(false);
    if (filterChild) {
      await fetchPosts(filterChild, date);
    }
  };

  const handleSelectedPostChange = (post: Post | null, skipConfirmDiscard?: boolean) => {
    const doChange = async () => {
      const proceed = skipConfirmDiscard || await confirmDiscard();
      if (!proceed) return;
      setSelectedPost(post);
      setHasUnsavedChanges(false);
      // Opening a post takes the menu's space; closing it doesn't give it
      // back. The menu only ever reopens when the user pins it.
      if (post != null) setSidebarOpen(false);
    };
    void doChange();
  }

  const handleReloadPosts = async (skipConfirmDiscard?: boolean) => {
    if (!filterChild) return;

    // Check if skipConfirmDiscard is strictly true (avoiding event objects)
    const shouldSkip = skipConfirmDiscard === true;
    const proceed = shouldSkip || await confirmDiscard();

    if (!proceed) return;
    await fetchPosts(filterChild);
  };

  const handleNewPost = async () => {
    if (!filterChild) return;

    if (!userRoleIds.includes(roles.Editor)) {
      alert("You do not have permission to create a new post.");
      return;
    }

    const proceed = await confirmDiscard();
    if (!proceed) return;

    if (!user) return;

    // A new post is stamped with the current date and time, so starting one
    // while the workspace is parked on an earlier period would save it out of
    // view. Move the period to the one containing today first — keeping the
    // user's mode where it still applies, this week otherwise — so what gets
    // written lands where they're looking. (confirmDiscard already ran above,
    // so the filter state is set directly rather than through the change
    // handlers, which would prompt a second time.)
    const now = new Date();
    const today = toDateString(now);
    const todayInView = !!filterFromDate && !!filterToDate
      && today >= filterFromDate && today <= filterToDate;

    if (todayInView === false) {
      const from = shortcut === 'today' ? today : toDateString(getMonday(now));
      const to = shortcut === 'today' ? today : toDateString(getFriday(now));
      if (shortcut !== 'today' && shortcut !== 'week') setShortcut('week');
      setFilterFromDate(from);
      setFilterToDate(to);
      setPosts([]);
      await fetchPosts(filterChild, from);
    }

    const newPost: Post = {
      id: -1,
      post_date: new Date().toISOString(),
      title: "",
      created_by: user.id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      text_content: "",
      classroom_id: null,
      uploads: [],
      child_ids: [parseInt(filterChild)],
      is_published: false,
    };

    setSelectedPost(newPost);
    setHasUnsavedChanges(true);
    setSidebarOpen(false);
  };

  const confirmDiscard = (): Promise<boolean> => {
    if (!hasUnsavedChanges) return Promise.resolve(true);
    setShowConfirmModal(true);
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
    });
  };

  // Help's "Take me there" and its section links move pages too, so they run
  // the same discard prompt the menu's links do.
  useHelpNavigationGuard(confirmDiscard);

  const handleConfirmModalConfirm = () => {
    if (confirmResolveRef.current) confirmResolveRef.current(true);
    setShowConfirmModal(false);
  };

  const handleConfirmModalCancel = () => {
    if (confirmResolveRef.current) confirmResolveRef.current(false);
    setShowConfirmModal(false);
  };

  // Filtering + ordering + search
  const filteredPosts = posts
    .filter(p => {
      const postDate = new Date(p.post_date);
      const from = filterFromDate ? new Date(filterFromDate) : null;
      let to = filterToDate ? new Date(filterToDate) : null;

      if (to) {
        to.setDate(to.getDate() + 1);
      }

      // The deleted view spans every period. What bounds it is deleted_at —
      // the seven-day restore window — not when the post was written. A post
      // from March deleted yesterday is exactly what someone is looking for,
      // and applying the week filter here would hide it even though the server
      // returned it. Search still applies: narrowing within deleted posts is
      // useful, narrowing them by a date the user isn't thinking about is not.
      const matchFrom = showingDeleted || !from || postDate >= from;
      const matchTo = showingDeleted || !to || postDate < to;

      const matchSearch =
        !searchTerm ||
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.text_content || '').toLowerCase().includes(searchTerm.toLowerCase());

      return matchFrom && matchTo && matchSearch;
    })
    .sort((a, b) => {
      const dateA = new Date(a.post_date).getTime();
      const dateB = new Date(b.post_date).getTime();
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });

  // Block /reports for users without the Reporting role. Wait for the roles
  // map to load before deciding — otherwise we'd redirect everyone on first
  // render. Once loaded, send unauthorised users to the dashboard.
  if (
    (viewMode === ViewModeEnum.Report || viewMode === ViewModeEnum.Insights) &&
    Object.keys(roles).length > 0 &&
    !userRoleIds.includes(roles.Reporting)
  ) {
    return <Navigate to="/" replace />;
  }

  // Insights also needs a plan that includes full AI. The nav item is hidden
  // without it, so guard the direct URL too — a nav gate alone leaves the page
  // one address-bar edit away. entitlements fails closed while loading, so this
  // errs toward the dashboard rather than flashing a premium page into view.
  if (viewMode === ViewModeEnum.Insights && !entitlements.aiFullEnabled) {
    return <Navigate to="/" replace />;
  }

  // Attendance Stats is hidden from support sessions; guard the URL too. The
  // figures are computed here from GET /api/attendance, which support keeps
  // for fixing records, so the API can't refuse this page on its own.
  if (viewMode === ViewModeEnum.AttendanceStats && activeUser?.isSupport) {
    return <Navigate to={viewToPath(ViewModeEnum.Attendance)} replace />;
  }

  // Block /schedule for external users (parents etc.) — the Weekly Schedule
  // nav item is hidden for them, so guard direct URL access too. Same
  // isExternal precedence as fetchChildProfiles/PostsGrid: trust the active
  // user's flag, falling back to the org only when the token lacks it.
  const actorIsExternal =
    activeUser?.isExternal === true ||
    (activeUser?.isExternal === undefined && selectedOrganisation?.isExternal === true);
  if (viewMode === ViewModeEnum.Schedule && actorIsExternal) {
    return <Navigate to="/" replace />;
  }

  // ── Full-area child picker ───────────────────────────────────────────────
  // Two separate questions, deliberately not one flag:
  //   · `viewRequiresChild` — the section is meaningless without a child, so
  //     the picker owns the content area until one is chosen.
  //   · `viewOffersChildPicker` — the picker can be summoned from the sidebar's
  //     compact/full toggle.
  // Schedule/attendance are meaningful with "All children" so they never
  // *require* a child — but they still deserve the full-screen browse instead
  // of hunting through the squashed 110px column. Conflating the two is what
  // previously denied them the toggle.
  const viewRequiresChild =
    viewMode === ViewModeEnum.Grid ||
    viewMode === ViewModeEnum.Insights ||
    (viewMode === ViewModeEnum.Report && !reportsOnHistoryTab) ||
    (viewMode === ViewModeEnum.Documents && !documentsOnOrgTab);

  const viewOffersChildPicker =
    viewRequiresChild ||
    viewMode === ViewModeEnum.Schedule ||
    viewMode === ViewModeEnum.Attendance ||
    viewMode === ViewModeEnum.AttendanceStats;

  const showChildPicker =
    (viewRequiresChild && !filterChild) || (viewOffersChildPicker && childPickerOpen);

  const childPickerSubtitle =
    viewMode === ViewModeEnum.Insights ? 'Choose whose insights you want to see.'
      : viewMode === ViewModeEnum.Report ? 'Choose who the report is for.'
      : viewMode === ViewModeEnum.Documents ? 'Choose whose documents you want to see.'
      : viewMode === ViewModeEnum.Schedule ? 'Choose whose sessions you want to see.'
      : viewMode === ViewModeEnum.Attendance ? 'Choose whose attendance you want to record.'
      : viewMode === ViewModeEnum.AttendanceStats ? 'Choose whose attendance figures you want to see.'
      : 'Choose whose posts you want to add or edit.';

  // A way out exists when there's a selection to return to — or when the
  // section is happy with "All children", so backing out isn't a dead end.
  const canDismissPicker = !!filterChild || !viewRequiresChild;

  // Where the picker gets rendered differs by section, and getting this wrong
  // collapses the sidebar with nothing to replace it:
  //   · require-a-child sections (posts, insights, reports, documents) own a
  //     tab strip, so they take `childPicker` as a prop and slot it under their
  //     own tabs — see the JSX below.
  //   · offer-only sections (schedule, attendance, attendance stats) have no
  //     tab strip to preserve, so the picker replaces the section outright.
  const pickerReplacesSection = showChildPicker && !viewRequiresChild;

  const childPicker = showChildPicker ? (
    <ChildSelectorPanel
      childProfiles={childProfiles}
      selectedChildId={filterChild}
      onSelectChild={(id) => handleChildChange(id)}
      subtitle={childPickerSubtitle}
      // Where "All children" is a real scope, the picker has to be able to say so.
      allowAll={!viewRequiresChild}
      onCollapse={canDismissPicker ? () => setChildPickerOpen(false) : undefined}
    />
  ) : null;

  // The compact column stands down while the picker owns the screen.
  const filtersStackCollapsed =
    showChildPicker ||
    (viewMode === ViewModeEnum.Documents && documentsOnOrgTab);

  return (
    <>
        {/* Sidebar (toggleable) - animated slide in/out */}
        {/* Pinned, or collapsed to the icon rail — never gone. */}
        <div className={`filters-panel ${sidebarOpen ? 'open' : 'is-rail'}`}>
          <SidebarFilters
            childProfiles={childProfiles}
            filterChild={filterChild}
            setFilterChild={handleChildChange}
            filterFromDate={filterFromDate}
            setFilterFromDate={handleFromDateChange}
            filterToDate={filterToDate}
            setFilterToDate={setFilterToDate}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            onClearFilters={() => {
              handleChildChange(null);
              setFilterFromDate(null);
              setFilterToDate(null);
              setSortOrder('desc');
              setSearchTerm('');
              setSelectedPost(null);
              setPosts([]);
            }}
            viewMode={viewMode}
            onNavigate={handleSidebarNavigate}
            shortcut={shortcut}
            setShortcut={setShortcut}
          />
        </div>

        {viewMode !== ViewModeEnum.Dashboard && (
          <div className={`filters-stack${filtersStackCollapsed ? ' filters-stack--collapsed' : ''}${sectionJustChanged ? ' filters-stack--instant' : ''}`}>
            <ChildProfilesSidebar
              onSelectChild={(id) => handleChildChange(id)}
              selectedChildId={filterChild}
              filtersVisible={sidebarOpen}
              onReload={handleReloadPosts}
              isReloading={isReloading}
              showClearAvatar={viewMode === ViewModeEnum.Schedule || viewMode === ViewModeEnum.Attendance || viewMode === ViewModeEnum.AttendanceStats || viewMode === ViewModeEnum.Insights}
              onExpandFull={viewOffersChildPicker ? handleExpandChildPicker : undefined}
            />
            {(viewMode === ViewModeEnum.Schedule || viewMode === ViewModeEnum.Attendance) && (
              <ScheduleTeacherFilter
                selectedTeacherId={filterTeacherId}
                onSelectTeacher={setFilterTeacherId}
              />
            )}
          </div>
        )}

        {/* Posts / Detail */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0px' }}>
          {postSaveMessage && (
            <div className="alert alert-success mb-0 px-4 py-2 rounded-0" style={{ flexShrink: 0 }}>
              {postSaveMessage}
            </div>
          )}
          <div className="top-tabs-page" style={{ flex: 1, display: 'flex', gap: '0px', minHeight: 0 }}>
          {pickerReplacesSection ? (
            childPicker
          ) : viewMode === ViewModeEnum.Dashboard ? (
            <Dashboard
              childProfiles={childProfiles}
              onViewChildPosts={(childId) => handleChildChange(childId)}
            />
          ) : viewMode === ViewModeEnum.AttendanceStats ? (
            <AttendanceCharts childProfiles={childProfiles} childIdFilter={filterChild} />
          ) : viewMode === ViewModeEnum.Documents ? (
            <DocumentsTabs childId={filterChild} childProfiles={childProfiles} onTabChange={(t) => setDocumentsOnOrgTab(t === 'organisation')} childPicker={childPicker} />
          ) : viewMode === ViewModeEnum.Insights ? (
            <ChildInsights childProfiles={childProfiles} childIdFilter={filterChild} childPicker={childPicker} />
          ) : viewMode === ViewModeEnum.Schedule ? (
            <WeeklySchedule
              childId={filterChild}
              childProfiles={childProfiles}
              weekStart={filterFromDate}
              onWeekChange={handleWeekChange}
              teacherFilterId={filterTeacherId}
            />
          ) : viewMode === ViewModeEnum.Attendance ? (
            <WeeklyAttendance
              childIdFilter={filterChild}
              childProfiles={childProfiles}
              weekStart={filterFromDate}
              onWeekChange={handleWeekChange}
              teacherFilterId={filterTeacherId}
            />
          ) : viewMode === ViewModeEnum.Report ? (
            <ReportsTabs
              posts={filteredPosts}
              filterChild={filterChild}
              filterFromDate={filterFromDate}
              filterToDate={filterToDate}
              childProfiles={childProfiles}
              onPeriodChange={handlePeriodChange}
              onReloadPosts={handleReloadPosts}
              notice={reportGenerateMessage}
              onNotice={setReportGenerateMessage}
              onTabChange={(t) => setReportsOnHistoryTab(t === 'history')}
              childPicker={childPicker}
            />
          ) : showChildPicker ? (
            childPicker
          ) : (
            <>
              {viewMode === ViewModeEnum.Grid && (
                <PostsGrid
                  posts={filteredPosts}
                  isLoading={isReloading}
                  filterChild={filterChild}
                  selectedPost={selectedPost}
                  setSelectedPost={handleSelectedPostChange}
                  setPosts={setPosts}
                  onNewPost={handleNewPost}
                  onReloadPosts={handleReloadPosts}
                  showingDeleted={showingDeleted}
                  onToggleDeleted={async (next: boolean) => {
                    setShowingDeleted(next);
                    setSelectedPost(null);
                    setPosts([]);
                    if (!filterChild) return;
                    // Fetched here rather than through fetchPosts because that
                    // reads showingDeleted from state, which has not committed
                    // yet on this tick.
                    const data = await PostDataService.getPosts(
                      filterChild,
                      next ? null : (filterFromDate || undefined),
                      next,
                    );
                    setPosts(data);
                  }}
                  onViewReport={() => handleViewModeChange(ViewModeEnum.Report)}
                  onDirtyChange={setHasUnsavedChanges}
                  onSaveSuccess={(msg) => setPostSaveMessage(msg)}
                  weekStart={filterFromDate}
                  filterToDate={filterToDate}
                  onWeekChange={handleWeekChange}
                  shortcut={shortcut}
                  setShortcut={setShortcut}
                  setFilterFromDate={handleFromDateChange}
                  setFilterToDate={setFilterToDate}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  sortOrder={sortOrder}
                  setSortOrder={setSortOrder}
                />
              )}
            </>
          )}
          </div>
        </div>

      <ConfirmationModal
        show={showConfirmModal}
        title="Unsaved Changes"
        message="You have unsaved changes that will be lost. Do you want to continue?"
        onConfirm={handleConfirmModalConfirm}
        onCancel={handleConfirmModalCancel}
        confirmText="Continue"
        cancelText="Cancel"
      />
    </>
  );
};

export default Home;
